import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

// ====== Filter multi-pilih (chip + checkbox) ======
// Dipakai di menu Tagihan (Nama Klien) dan menu CM (Nama Klien & No Kontrak).
// Nilai `selected` selalu berupa array; `onChange(array)` mengembalikan array baru.
//
// PENTING: panel pilihannya di-render lewat **portal ke <body>** dengan posisi `fixed`.
// Alasannya: baris filter (mis. di menu CM) memakai `overflow: auto` sehingga panel
// `position: absolute` akan TERPOTONG kontainer itu (harus di-scroll untuk melihat
// pilihan). Dengan portal, panel selalu tampil di depan kotak filternya.
const PANEL_MAX_H = 220;
const HEAD_H = 44; // tinggi bagian input pencarian

function MultiSelectFilter({ options, selected = [], onChange, placeholder, searchPlaceholder }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pos, setPos] = useState(null);
  const ref = useRef(null);      // kotak pemicu
  const panelRef = useRef(null); // panel (ada di portal, di luar `ref` → dicek terpisah)

  // Hitung posisi panel mengikuti kotak pemicunya. Kalau ruang di bawah sempit,
  // panel dibuka ke atas supaya tetap terlihat tanpa perlu scroll.
  const hitungPosisi = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const lebar = Math.max(r.width, 230);
    const ruangBawah = window.innerHeight - r.bottom - 8;
    const ruangAtas = r.top - 8;
    const bukaKeAtas = ruangBawah < 140 && ruangAtas > ruangBawah;
    const tersedia = bukaKeAtas ? ruangAtas : ruangBawah;
    setPos({
      left: Math.max(8, Math.min(r.left, window.innerWidth - lebar - 8)),
      width: lebar,
      ...(bukaKeAtas
        ? { bottom: window.innerHeight - r.top + 4 }
        : { top: r.bottom + 4 }),
      maxHeight: Math.max(120, Math.min(PANEL_MAX_H, tersedia)),
    });
  }, []);

  // Tutup saat klik di luar komponen (pemicu ATAU panel)
  useEffect(() => {
    const handler = (e) => {
      const diPemicu = ref.current && ref.current.contains(e.target);
      const diPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!diPemicu && !diPanel) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Saat terbuka: hitung posisi sebelum paint, lalu ikuti scroll/resize.
  // `capture: true` dipakai supaya scroll di dalam kontainer (tabel/baris filter) ikut tertangkap.
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    hitungPosisi();
    const onMove = () => hitungPosisi();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, hitungPosisi]);

  const daftar = Array.isArray(options) ? options : [];
  const filteredOptions = daftar.filter(o =>
    String(o).toLowerCase().includes(search.toLowerCase())
  );

  const toggleOption = (opt) => {
    if (selected.includes(opt)) {
      onChange(selected.filter(s => s !== opt));
    } else {
      onChange([...selected, opt]);
    }
  };

  return (
    <div ref={ref} className="ms-filter" data-ms-filter={placeholder || 'filter'} style={{ position: 'relative', minWidth: 160 }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          minHeight: 32, cursor: 'pointer', border: '1px solid #d1d5db', borderRadius: 6,
          padding: '4px 8px', display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center',
          fontSize: '0.82rem', background: '#fff'
        }}
      >
        {selected.length === 0 ? (
          <span style={{ color: '#9ca3af' }}>{placeholder || 'Pilih...'}</span>
        ) : (
          selected.map(s => (
            <span key={s} style={{
              display: 'inline-flex', alignItems: 'center', gap: 2,
              background: '#e0e7ff', color: '#3730a3', borderRadius: 4, padding: '1px 6px', fontSize: '0.75rem'
            }}>
              {s}
              <X
                size={12} style={{ cursor: 'pointer' }}
                onClick={(e) => { e.stopPropagation(); onChange(selected.filter(x => x !== s)); }}
              />
            </span>
          ))
        )}
        <span style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: '0.7rem' }}>
          {selected.length > 0 ? `(${selected.length})` : ''}
        </span>
      </div>
      {open && pos && createPortal(
        <div
          ref={panelRef}
          data-ms-panel={placeholder || 'filter'}
          style={{
            position: 'fixed', zIndex: 4000,
            top: pos.top, bottom: pos.bottom, left: pos.left, width: pos.width,
            background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)'
          }}
        >
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={searchPlaceholder || 'Cari...'}
            style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', border: 'none', borderBottom: '1px solid #e5e7eb', fontSize: '0.8rem', borderRadius: '6px 6px 0 0' }}
            onClick={e => e.stopPropagation()}
          />
          <div style={{ maxHeight: Math.max(80, pos.maxHeight - HEAD_H), overflowY: 'auto' }}>
            {filteredOptions.length === 0 ? (
              <div style={{ padding: '8px', color: '#9ca3af', fontSize: '0.8rem', textAlign: 'center' }}>Tidak ada data</div>
            ) : (
              filteredOptions.map(opt => (
                <label key={opt} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', cursor: 'pointer',
                  fontSize: '0.8rem', background: selected.includes(opt) ? '#eef2ff' : 'transparent',
                  transition: 'background 0.15s'
                }}
                  onMouseEnter={e => { if (!selected.includes(opt)) e.target.style.background = '#f3f4f6'; }}
                  onMouseLeave={e => { if (!selected.includes(opt)) e.target.style.background = 'transparent'; }}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(opt)}
                    onChange={() => toggleOption(opt)}
                    style={{ margin: 0, accentColor: '#5e72e4' }}
                  />
                  {opt}
                </label>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default MultiSelectFilter;
